/**
 * Format Converters for Content Blocks
 *
 * Functions to convert between internal UI format and Anthropic API format.
 * Used for API requests and loading message history.
 *
 * @module lib/storage/format-converters
 */

import type {
  AnthropicImageContent,
  AnthropicDocumentContent,
} from "@/features/chat/types/anthropic";
import type { Base64ContentBlock, StorageImageBlock, StoragePdfBlock } from "@/features/chat/types/messages";
import {
  isAnthropicImageContent,
  isAnthropicDocumentContent,
} from "./type-guards";

// =============================================================================
// TO ANTHROPIC FORMAT
// =============================================================================

/**
 * Convert internal Base64ContentBlock to Anthropic API format
 */
export function toAnthropicFormat(
  block: Base64ContentBlock
):
  | AnthropicImageContent
  | AnthropicDocumentContent
  | (AnthropicImageContent & { metadata?: Record<string, unknown> }) {
  if (block.type === "image" && block.mime_type.startsWith("image/")) {
    const result: AnthropicImageContent & { metadata?: Record<string, unknown> } = {
      type: "image",
      source: {
        type: "base64",
        media_type: block.mime_type,
        data: block.data,
      },
    };

    // Include metadata with description if present
    if (block.metadata) {
      result.metadata = block.metadata;
    }

    return result;
  }

  if (block.type === "file" && block.mime_type === "application/pdf") {
    return {
      type: "document",
      source: {
        type: "base64",
        media_type: "application/pdf",
        data: block.data,
      },
      title: block.metadata?.filename || block.metadata?.name || "document.pdf",
      citations: {
        enabled: true,
      },
    };
  }

  throw new Error(`Unsupported content block type: ${block.type}`);
}

/**
 * Convert storage PDF block to Anthropic API format (with storage source)
 */
export function storagePdfBlockToAnthropicFormat(block: StoragePdfBlock): {
  type: "document";
  source: { type: "storage"; relative_gcs_path: string; media_type: string };
  title: string;
  citations: { enabled: boolean };
  thumbnail?: string;
} {
  return {
    type: "document",
    source: {
      type: "storage",
      relative_gcs_path: block.metadata?.storagePath || "",
      media_type: "application/pdf",
    },
    title: block.metadata?.filename || block.metadata?.name || "document.pdf",
    citations: {
      enabled: true,
    },
    thumbnail: block.thumbnail,
  };
}

/**
 * Convert storage image block to Anthropic API format (with storage source)
 * Expects frontend format: source_type="storage" + fileName + relative_gcs_path + mime_type at root
 */
export function storageImageBlockToAnthropicFormat(block: StorageImageBlock): {
  type: "image";
  source: { type: "storage"; relative_gcs_path: string; media_type: string };
  metadata?: Record<string, unknown>;
  thumbnail?: string;
} {
  // Validate required fields (frontend format)
  if (!block.relative_gcs_path || !block.mime_type) {
    throw new Error(
      "Invalid StorageImageBlock: missing relative_gcs_path or mime_type"
    );
  }

  const result: {
    type: "image";
    source: { type: "storage"; relative_gcs_path: string; media_type: string };
    metadata?: Record<string, unknown>;
    thumbnail?: string;
  } = {
    type: "image",
    source: {
      type: "storage",
      relative_gcs_path: block.relative_gcs_path,
      media_type: block.mime_type,
    },
  };

  // Include metadata with description if present, filtering out undefined values
  if (block.metadata) {
    const filteredMetadata = Object.fromEntries(
      Object.entries(block.metadata).filter(([_, v]) => v !== undefined)
    );
    if (Object.keys(filteredMetadata).length > 0) {
      result.metadata = filteredMetadata;
    }
  }

  // Include thumbnail for display after reload
  if (block.thumbnail) {
    result.thumbnail = block.thumbnail;
  }

  return result;
}

// =============================================================================
// FROM ANTHROPIC FORMAT
// =============================================================================

/**
 * Convert Anthropic API format back to internal Base64ContentBlock.
 * Used when loading message history from Firebase.
 */
export function fromAnthropicFormat(block: unknown): Base64ContentBlock | null {
  if (typeof block !== "object" || block === null) return null;
  const b = block as Record<string, unknown>;
  const source = b.source as Record<string, unknown> | undefined;
  const metadata = b.metadata as Record<string, unknown> | undefined;

  // Handle Anthropic image format
  if (isAnthropicImageContent(block)) {
    const imgSource = source as { media_type: string; data: string };
    return {
      type: "image",
      source_type: "base64",
      mime_type: imgSource.media_type,
      data: imgSource.data,
      thumbnail: (b.thumbnail || metadata?.thumbnail) as string | undefined,
      metadata: {
        name: "image",
        ...metadata,
      },
    };
  }

  // Handle Anthropic document format
  if (isAnthropicDocumentContent(block)) {
    const docSource = source as { media_type: string; data: string };
    const thumbnail = (b.thumbnail || metadata?.thumbnail) as string | undefined;
    return {
      type: "file",
      source_type: "base64",
      mime_type: docSource.media_type,
      data: docSource.data,
      thumbnail,
      metadata: {
        filename: (b.title as string) || "document.pdf",
        name: (b.title as string) || "document.pdf",
        ...metadata,
      },
    };
  }

  // Handle storage references (loaded from Firestore)
  // Note: This returns a StorageImageBlock disguised as Base64ContentBlock for backward compatibility
  if (b.type === "image" && source?.type === "storage") {
    const thumbnail = (b.thumbnail || metadata?.thumbnail) as string | undefined;
    const relativePath = source.relative_gcs_path as string || "";
    // Extract fileName from relative_gcs_path (e.g., "images/filename.png" -> "filename.png")
    const fileName = relativePath.split("/").pop() || "image.png";

    return {
      type: "image",
      source_type: "storage",
      mime_type: (source.media_type as string) || "image/jpeg",
      fileName, // REQUIRED for StorageImageBlock
      relative_gcs_path: relativePath,
      thumbnail,
      metadata: {
        name: fileName,
        description: b.description as string | undefined,
        storagePath: relativePath,
        ...metadata,
      },
    } as unknown as Base64ContentBlock; // Type cast to maintain backward compatibility
  }

  // Handle storage document references with thumbnail
  if (
    b.type === "document" &&
    source?.type === "storage" &&
    (b.thumbnail || metadata?.thumbnail)
  ) {
    const thumbnail = (b.thumbnail || metadata?.thumbnail) as string;
    return {
      type: "file",
      source_type: "base64",
      mime_type: (source.media_type as string) || "application/pdf",
      data: thumbnail,
      thumbnail,
      metadata: {
        filename: (b.title as string) || "document.pdf",
        name: (b.title as string) || "document.pdf",
        relative_gcs_path: source.relative_gcs_path as string,
        ...metadata,
      },
    };
  }

  return null;
}
